import { ReactElement, useCallback, useEffect, useState } from 'react';
import { Flex } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { RowSelectMethod } from 'antd/es/table/interface';
import { AnyObject } from 'antd/es/_util/type';
import InfiniteScroll from 'react-infinite-scroll-component';
import { MSkeleton } from '@Components/Base';
import { RequestListSearchResponse } from '@Types/api/requestList';
import {
  HrmRequestFormResponse,
  ITRequestFormResponse,
  MaterialRequisitionFormResponse,
  ResignationRequestFormResponse,
} from '@Types/api/form';
import { AttendanceProfileResponse } from '@Types/api/profile/AttendanceProfileList';

import RequestType from '@Types/common/RequestTypes';
import { RequestListContext } from '@Components/types/RequestListContext';
import {
  ApprovalsTasksRequisitionResponse,
  ApprovalsTasksResponse,
} from '@Types/api/approvalsList';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

import useFindRequestType from '@Hooks/useFindRequestType';
import useSmartMapper from './hook/useSmartMapper';
import Card from './components/Card';
import styles from './request-list-mobile.module.css';
import { NoData } from '../noData';
import ApprovalBulkSection from './components/ApprovalBulkSection';

type ListRequests = RequestListSearchResponse<
  | HrmRequestFormResponse
  | ITRequestFormResponse
  | ResignationRequestFormResponse
  | MaterialRequisitionFormResponse
  | AttendanceProfileResponse
  | ApprovalsTasksResponse
  | ApprovalsTasksRequisitionResponse
>;

type RequestsDataList = Array<
  | HrmRequestFormResponse
  | ITRequestFormResponse
  | ResignationRequestFormResponse
  | MaterialRequisitionFormResponse
  | ApprovalsTasksResponse
  | ApprovalsTasksRequisitionResponse
  | AttendanceProfileResponse
>;

export interface RequestListMobileProps {
  listRequests?: ListRequests;
  requestsDataList?: RequestsDataList;
  requestType: RequestType;
  onEdit?: (recordKey: string) => void;
  onSubmit?: (requestNo: string) => void;
  onView?: (recordKey: string) => void;
  onCancel?: (recordKey: string) => void;
  onCopy?: (requestNo: string, mrNo: string) => void;
  onDelete?: (requestNo: string) => void;
  onApproveReject?: () => void;
  onApprovalHistory?: (requestNo: string, isIt?: boolean) => void;
  onLoadMore?: (nextPage?: number) => void;
  onSelectTask?: (
    selectedRowKeys: React.Key[],
    selectedRows: AnyObject[],
    info: {
      type: RowSelectMethod;
    }
  ) => void;
  onBulkApprove?: () => void;
  onBulkReject?: () => void;
  showAction?: boolean;
  ownActions?: ReactElement | null;
  context?: RequestListContext;
  isFiltersApplied?: boolean;
  isSearchApplied?: boolean;
  selectedTasksRow?:
    | ApprovalsTasksResponse[]
    | ApprovalsTasksRequisitionResponse[];
}

const RequestListMobile = ({
  listRequests,
  requestsDataList,
  requestType,
  showAction = true,
  onEdit,
  onSubmit,
  onView,
  onCancel,
  onCopy,
  onDelete,
  onApprovalHistory,
  onApproveReject,
  onLoadMore,
  onSelectTask,
  onBulkApprove,
  onBulkReject,
  selectedTasksRow,
  context = 'my-requests',
  isFiltersApplied = false,
  isSearchApplied = false,
  ownActions,
}: RequestListMobileProps): JSX.Element | null => {
  const { transformItem } = useSmartMapper(requestType);
  const [nextPage, setNextPage] = useState(1);
  const [showActions, setShowActions] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState<
    Array<ApprovalsTasksResponse | ApprovalsTasksRequisitionResponse>
  >([]);
  const { findRequestTypeBy } = useFindRequestType();

  // reset to default when scroll is ended
  useEffect(() => {
    if (listRequests?.totalPages) {
      const lastPage = listRequests.totalPages;
      if (lastPage === nextPage) {
        setNextPage(1);
      }
    }
  }, [listRequests?.totalPages, nextPage]);

  const handleNextFn = useCallback(() => {
    setNextPage(nextPage + 1);
    onLoadMore?.(nextPage);
  }, [onLoadMore, nextPage]);

  const handleShowActions = (): void => {
    setShowActions(!showActions);
    setSelectedTasks([]);
  };

  const handleCheckboxChange = (
    selectedTask: ApprovalsTasksResponse | ApprovalsTasksRequisitionResponse
  ): void => {
    setSelectedTasks((prevSelectedItems) => {
      const essPrevSelectedItems =
        prevSelectedItems as ApprovalsTasksResponse[];
      const mrPrevSelectedItems =
        prevSelectedItems as ApprovalsTasksRequisitionResponse[];

      const { requestId } = selectedTask as ApprovalsTasksResponse;
      const { reqId } = selectedTask as ApprovalsTasksRequisitionResponse;

      if (reqId) {
        if (
          mrPrevSelectedItems.find(
            (selectedItem) => selectedItem.reqId === reqId
          )
        ) {
          // If the item is already selected, remove it
          return mrPrevSelectedItems.filter((item) => item.reqId !== reqId);
        }

        // If the item is not selected, add it
        return [...mrPrevSelectedItems, selectedTask];
      }

      if (
        essPrevSelectedItems.find(
          (selectedItem) => selectedItem.requestId === requestId
        )
      ) {
        // If the item is already selected, remove it
        return essPrevSelectedItems.filter(
          (item) => item.requestId !== requestId
        );
      }

      // If the item is not selected, add it
      return [...essPrevSelectedItems, selectedTask];
    });
  };

  const handleSelectAllTasks = (event: CheckboxChangeEvent): void => {
    const { target } = event;
    if (target.checked) {
      setSelectedTasks(
        requestsDataList as
          | ApprovalsTasksResponse[]
          | ApprovalsTasksRequisitionResponse[]
      );
    } else {
      setSelectedTasks([]);
    }
  };

  const isSelectedItem = useCallback(
    (
      item: ApprovalsTasksResponse | ApprovalsTasksRequisitionResponse
    ): boolean => {
      const essSelectedTasksRow = selectedTasksRow as ApprovalsTasksResponse[];
      const mrSelectedTasksRow =
        selectedTasksRow as ApprovalsTasksRequisitionResponse[];

      const { requestId } = item as ApprovalsTasksResponse;
      const { reqId } = item as ApprovalsTasksRequisitionResponse;
      let selectedItem;

      if (reqId) {
        selectedItem = mrSelectedTasksRow?.find(
          (taskItem) => taskItem.reqId === reqId
        );

        return Boolean(selectedItem);
      }

      selectedItem = essSelectedTasksRow?.find(
        (taskItem) => taskItem.requestId === requestId
      );

      return Boolean(selectedItem);
    },
    [selectedTasksRow]
  );

  useEffect(() => {
    if (onSelectTask && showAction) {
      // first param not affect something.
      onSelectTask([], selectedTasks, { type: 'none' });
    }
  }, [selectedTasks]);

  if (
    !listRequests?.content ||
    !listRequests?.content?.length ||
    !requestsDataList?.length
  ) {
    return (
      <NoData
        context={context}
        isFiltersApplied={isFiltersApplied}
        isSearchApplied={isSearchApplied}
      />
    );
  }

  const actions = [
    {
      key: '0',
      label: (
        <Flex
          className="trojan-more-action-item"
          onClick={() => onBulkApprove?.()}
        >
          <CheckCircleOutlined />
          <span>Approve</span>
        </Flex>
      ),
    },

    {
      key: '1',
      label: (
        <Flex
          className="trojan-more-action-item"
          onClick={() => onBulkReject?.()}
        >
          <CloseCircleOutlined />
          <span>Reject</span>
        </Flex>
      ),
    },
  ];

  return (
    <div className={styles.requestListMobile}>
      <Flex gap={20} vertical>
        {context === 'approvals' && (
          <ApprovalBulkSection
            showActions={showActions}
            onShow={handleShowActions}
            onSelectAll={handleSelectAllTasks}
            countSelectedTasks={selectedTasks.length}
            actions={actions}
          />
        )}
        <InfiniteScroll
          next={handleNextFn}
          hasMore={requestsDataList.length < listRequests?.totalElements}
          loader={<MSkeleton paragraph={{ rows: 1 }} active />}
          dataLength={requestsDataList.length}
          scrollableTarget="scrollableList"
          endMessage={
            <p style={{ textAlign: 'center', textTransform: 'uppercase' }}>
              No more data
            </p>
          }
        >
          {requestsDataList?.map((item, index) => {
            const hrmItem = item as
              | HrmRequestFormResponse
              | ITRequestFormResponse
              | ResignationRequestFormResponse;

            const requisitionItem = item as MaterialRequisitionFormResponse;

            const approvalRequisitionItem =
              item as ApprovalsTasksRequisitionResponse;

            let requestItemId =
              hrmItem.requestId ||
              requisitionItem.requisitionId ||
              approvalRequisitionItem.reqId;

            // pre-set key value for Attendance Profile items
            if (!requestItemId) {
              requestItemId = index;
            }

            const requestNum = requisitionItem.reqNo;

            const {
              requestType: requestTypeCategory,
              status,
              items,
            } = transformItem(item);

            let recordItem = item as
              | ApprovalsTasksResponse
              | ApprovalsTasksRequisitionResponse;

            if ((item as ApprovalsTasksRequisitionResponse)?.service === 'MR') {
              recordItem = {
                ...(item as ApprovalsTasksRequisitionResponse),
                requestTypeId: 'MRRequest',
              };
            } else {
              recordItem = {
                ...(item as ApprovalsTasksResponse),
                requestTypeId: findRequestTypeBy(
                  (item as ApprovalsTasksResponse).requestTypeId,
                  'codeNum'
                )?.codeDesc,
              };
            }

            return (
              <Card
                key={requestItemId}
                context={context}
                requestType={requestType}
                requestTypeCategory={requestTypeCategory}
                status={status}
                requestNo={requestItemId}
                items={items}
                recordItem={recordItem}
                showAction={showAction}
                ownActions={ownActions}
                onEdit={() => onEdit?.(String(requestItemId))}
                onSubmit={() => onSubmit?.(String(requestItemId))}
                onView={() => onView?.(String(requestItemId))}
                onDelete={() => onDelete?.(String(requestItemId))}
                onCancel={() => onCancel?.(String(requestItemId))}
                onCopy={
                  requestNum
                    ? () => onCopy?.(String(requestItemId), String(requestNum))
                    : undefined
                }
                onApprovalHistory={onApprovalHistory}
                onApproveReject={onApproveReject}
                onSelectTask={() =>
                  handleCheckboxChange(
                    item as
                      | ApprovalsTasksResponse
                      | ApprovalsTasksRequisitionResponse
                  )
                }
                isSelected={isSelectedItem(
                  item as
                    | ApprovalsTasksResponse
                    | ApprovalsTasksRequisitionResponse
                )}
                showCheckbox={showActions}
              />
            );
          })}
        </InfiniteScroll>
      </Flex>
    </div>
  );
};

export default RequestListMobile;
